import { useAuthStore } from "../stores/useAuthStore";
import { AuthService } from "../data/services/AuthService";

// Shared in-flight lock: every caller goes through this one function so
// concurrent refresh attempts share a single network request instead of
// each firing their own (the refresh cookie rotates on use, so two
// concurrent requests would make the backend treat the second as reuse).
let refreshInFlight: Promise<string | null> | null = null;

// Name of the cross-tab lock acquired below — just a key into the browser's
// shared lock registry, doesn't need to mean anything beyond being unique.
const REFRESH_LOCK_NAME = "auth-refresh-token-rotation";

async function performRefresh(): Promise<string | null> {
  const doRefresh = async (): Promise<string | null> => {
    try {
      const accessToken = await AuthService.refreshToken();
      useAuthStore.getState().setTokens(accessToken);
      return accessToken;
    } catch {
      // Refresh token expired, revoked, reused, or missing — full logout.
      useAuthStore.getState().clearAuth();
      return null;
    }
  };

  // WHY THIS LOCK EXISTS:
  // The refresh cookie is browser-scoped, shared by every open tab — not
  // per-tab. refreshInFlight above only dedupes concurrent calls WITHIN one
  // tab; it does nothing for two tabs refreshing at nearly the same moment,
  // each reading the cookie before either rotation lands (e.g. the push
  // architecture's SSE reconnect ties refresh timing to the shared ~30-min
  // token TTL across tabs). Confirmed against the real backend that the
  // second tab's now-stale cookie doesn't just fail that one call — reuse
  // detection revokes the whole refresh token family, logging out every tab
  // on its next refresh, not just the one that lost the race.
  //
  // navigator.locks.request() is a browser-wide mutex (unlike
  // refreshInFlight, which is just a module-level variable scoped to this
  // tab's own JS heap), so gating the network call behind it means no tab
  // starts its own refresh until every other tab's has fully settled. By
  // the time a waiting tab's request actually goes out, the browser attaches
  // whatever cookie is current — never the stale one it originally read —
  // so the backend never sees a reuse and never revokes the family.
  //
  // Falls back to calling directly where the Web Locks API isn't available
  // (e.g. older browsers, some test environments) — same behavior as before
  // this lock existed, just without the cross-tab protection.
  if (typeof navigator === "undefined" || !navigator.locks) {
    return doRefresh();
  }
  return navigator.locks.request(REFRESH_LOCK_NAME, doRefresh);
}

/**
 * Refresh the access token using the httpOnly refresh cookie.
 *
 * Safe to call from any number of places at any time: if a refresh is
 * already in flight, every caller awaits and receives the result of that
 * same request instead of starting a new one. Once the in-flight request
 * settles, the next call starts a fresh one.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}
